import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// KeywordIP palette
const OLIVE = '#3d4128';
const SAGE = '#b9c096';
const CREAM = '#fbf8f3';
const INK = '#1f2937';
const MUTE = '#64748b';

function fmt(n) {
  if (n === null || n === undefined) return '\u2014';
  return Number(n).toLocaleString();
}

// Signed change, plain and honest: +N / -N / no change. Colour encodes direction,
// never hides it. Lower position is better, but these deltas are all count metrics
// (clicks, keywords, impressions) where up = good.
function Delta({ change }) {
  if (change === null || change === undefined || change === 0) {
    return <span style={{ fontSize: 12, color: MUTE, fontWeight: 600 }}>no change</span>;
  }
  const up = change > 0;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: up ? '#166534' : '#991b1b' }}>
      {up ? '\u25b2 +' : '\u25bc '}{Math.abs(change).toLocaleString()}
    </span>
  );
}

function HeadlineCard({ label, value, change, hint }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', flex: '1 1 160px', minWidth: 160 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: MUTE, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: INK, lineHeight: 1, marginBottom: 6 }}>{fmt(value)}</div>
      <Delta change={change} />
      {hint && <div style={{ fontSize: 11, color: MUTE, marginTop: 6, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}

// Describe a mover's prior standing truthfully: a real prior position means a climb;
// no prior signal means it is newly ranking. Never overstate a climb as an emergence.
function priorLabel(prevPosition) {
  if (prevPosition === null || prevPosition === undefined) return 'newly ranking';
  return 'climbed from ' + Number(prevPosition).toFixed(1);
}

function MoverRow({ m, tone }) {
  const good = tone === 'up';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: INK, wordBreak: 'break-word' }}>{m.keyword}</div>
        <div style={{ fontSize: 12, color: MUTE, marginTop: 2 }}>{priorLabel(m.prev_position)}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: good ? '#166534' : '#991b1b' }}>#{Number(m.position).toFixed(1)}</div>
        <div style={{ fontSize: 11, color: MUTE, marginTop: 2 }}>{fmt(m.impressions)} views {'\u00b7'} {fmt(m.clicks)} clicks</div>
      </div>
    </div>
  );
}

export default function MonthlyReport({ websiteId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getMonthlyReport(websiteId);
      setReport(data);
    } catch (e) {
      setError(e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message);
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  if (!websiteId) {
    return <div style={{ padding: 40, color: MUTE, fontSize: 14 }}>Select a website to see its monthly report.</div>;
  }
  if (loading && !report) {
    return <div style={{ padding: 40, color: MUTE, fontSize: 14 }}>Preparing the monthly report{'\u2026'}</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ color: '#991b1b', fontSize: 14, marginBottom: 12 }}>Could not build the report: {error}</div>
        <button onClick={load} style={{ padding: '8px 18px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }
  if (!report) return null;

  const h = report.headline || {};
  const winners = report.new_on_page_1 || [];
  const slipped = report.slipped || [];

  return (
    <div>
      {/* Print rules: hide app chrome, keep the report clean on paper */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #monthly-report, #monthly-report * { visibility: visible; }
          #monthly-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
          .no-print { display: none !important; }
          nav, header { display: none !important; }
        }
      `}</style>

      <div id="monthly-report" style={{ padding: '20px 24px', maxWidth: 940 }}>
        {/* Header — KeywordIP branding */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, borderBottom: '2px solid ' + OLIVE, paddingBottom: 14, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: OLIVE, textTransform: 'uppercase', letterSpacing: '1px' }}>KeywordIP</div>
            <h1 style={{ margin: '4px 0 2px', fontSize: 24, color: INK, fontWeight: 800 }}>Monthly Search Report</h1>
            <div style={{ fontSize: 13, color: MUTE }}>{report.domain} {'\u00b7'} {report.report_month}</div>
          </div>
          <button
            className="no-print"
            onClick={() => window.print()}
            style={{ padding: '9px 18px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            Download PDF
          </button>
        </div>

        {/* Verdict — derived from real deltas, never softened */}
        <div style={{ background: CREAM, border: '1px solid ' + SAGE, borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: OLIVE, lineHeight: 1.5 }}>{report.verdict}</div>
          <div style={{ fontSize: 12.5, color: MUTE, marginTop: 4 }}>Compared with {report.comparison_month}.</div>
        </div>

        {/* Headline metrics */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          <HeadlineCard label="Keywords on page 1" value={h.on_page_1 && h.on_page_1.value} change={h.on_page_1 && h.on_page_1.change} hint="Ranked in the top 10" />
          <HeadlineCard label="Keywords in top 3" value={h.in_top_3 && h.in_top_3.value} change={h.in_top_3 && h.in_top_3.change} hint="The most valuable positions" />
          <HeadlineCard label="Impressions" value={h.impressions && h.impressions.value} change={h.impressions && h.impressions.change} hint="Times shown in Google" />
          <HeadlineCard label="Clicks" value={h.clicks && h.clicks.value} change={h.clicks && h.clicks.change} hint="Visits from Google" />
          <HeadlineCard label="Tracked keywords" value={h.signal_keywords && h.signal_keywords.value} change={h.signal_keywords && h.signal_keywords.change} hint="With meaningful search volume" />
        </div>

        {/* New on page 1 */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: OLIVE, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Reached page 1 this month</div>
          <div style={{ fontSize: 12.5, color: MUTE, marginBottom: 6 }}>Keywords that moved into the top 10, most-seen first.</div>
          {winners.length === 0 ? (
            <div style={{ fontSize: 13, color: MUTE, paddingTop: 8 }}>No keywords reached page 1 this month.</div>
          ) : (
            winners.map(m => <MoverRow key={m.keyword} m={m} tone="up" />)
          )}
        </div>

        {/* Slipped */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Slipped off page 1</div>
          <div style={{ fontSize: 12.5, color: MUTE, marginBottom: 6 }}>Keywords that dropped out of the top 10 and are being worked on.</div>
          {slipped.length === 0 ? (
            <div style={{ fontSize: 13, color: MUTE, paddingTop: 8 }}>Nothing slipped off page 1 this month.</div>
          ) : (
            slipped.map(m => <MoverRow key={m.keyword} m={m} tone="down" />)
          )}
        </div>

        {/* Honest footer */}
        <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.6, marginTop: 16 }}>
          Figures cover {report.report_month} versus {report.comparison_month} and count only keywords with at least
          10 impressions in the month, so low-noise terms do not distort the totals. Branded searches (people looking
          for the business by name) are excluded, so these numbers reflect SEO performance rather than existing name
          recognition. Rankings and impressions typically move before clicks; a new page-1 ranking often shows few
          clicks in its first month.
        </div>
        <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 10 }}>Prepared by KeywordIP {'\u00b7'} {new Date(report.generated_at + 'Z').toLocaleDateString()}</div>
      </div>
    </div>
  );
}
